import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useRef } from "react";
import { ArrowRight, Phone, Star } from "lucide-react";
import { CallButton, Nav } from "@/components/site/nav";
import { Counter, MaskImage, Reveal } from "@/components/site/reveal";
import { PalletStack } from "@/components/site/pallet-stack";
import { SectionLabel } from "@/components/site/section-label";
import { EMAIL, PHONE_HREF } from "@/components/site/contact";
import usedPallets640 from "@/assets/used-pallets-640.webp";
import usedPallets960 from "@/assets/used-pallets-960.webp";
import usedPallets1280 from "@/assets/used-pallets-1280.webp";
import newPallets640 from "@/assets/new-pallets-640.webp";
import newPallets800 from "@/assets/new-pallets-800.webp";
import comboPallets640 from "@/assets/combo-pallets-640.webp";
import comboPallets960 from "@/assets/combo-pallets-960.webp";
import comboPallets1280 from "@/assets/combo-pallets-1280.webp";

function importHomeBelow() {
  return import("@/components/site/home-below").then((m) => ({ default: m.HomeBelow }));
}

/** SSR loads immediately; on the client, wait for idle + yield so index.js hydration isn't one long task. */
const HomeBelow = lazy(() => {
  if (typeof window === "undefined") return importHomeBelow();
  return new Promise<Awaited<ReturnType<typeof importHomeBelow>>>((resolve) => {
    const run = () => {
      void import("@/lib/yield-to-main")
        .then((m) => m.yieldToMain())
        .then(() => importHomeBelow())
        .then(resolve);
    };
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(run, { timeout: 3_500 });
    } else {
      window.setTimeout(run, 50);
    }
  });
});

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      {
        title: "Livengood Pallet Service | Pallets in Frankfort, Indiana",
      },
      {
        name: "description",
        content:
          "Used, new, and combo pallets for Central Indiana businesses, plus pallet removal and recycling. Family-owned in Frankfort since 1984. Call (765) 326-0111.",
      },
      {
        property: "og:title",
        content: "Livengood Pallet Service | Pallets in Frankfort, Indiana",
      },
      {
        property: "og:description",
        content:
          "Pallets in stock, priced right, ready now. Used, new, and combo pallets plus pallet removal and recycling from a family operation with 40+ years in the trade.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://livengoodpalletservice.com/" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://livengoodpalletservice.com/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "Livengood Pallet Service",
          description:
            "Used, new, and combo pallets for Central Indiana businesses, plus pallet removal and recycling. Family-owned in Frankfort, Indiana since 1984.",
          url: "https://livengoodpalletservice.com/",
          telephone: "+1-765-326-0111",
          email: EMAIL,
          foundingDate: "1984",
          founder: { "@type": "Person", name: "Jerry Livengood" },
          address: {
            "@type": "PostalAddress",
            streetAddress: "9024 W ST RD 28",
            addressLocality: "Frankfort",
            addressRegion: "IN",
            postalCode: "46041",
            addressCountry: "US",
          },
          areaServed: { "@type": "AdministrativeArea", name: "Central Indiana" },
        }),
      },
    ],
  }),
  component: Index,
});

function HeroHeadline() {
  const lines = [["Pallets", "in"], ["stock."], ["Priced", "right."], ["Ready", "now."]] as const;
  return (
    <h1 className="hero-headline mt-6 leading-none text-bone">
      {lines.map((words, lineIndex) => (
        <span key={lineIndex} className="hero-line">
          {words.map((w) => (
            <span key={w} className="inline-block pr-[0.28em] last:pr-0">
              <span className={w === "now." ? "text-amber-brand" : undefined}>{w}</span>
            </span>
          ))}
        </span>
      ))}
    </h1>
  );
}

function Hero() {
  const visual = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const node = visual.current;
    if (!node) return;

    let raf = 0;
    let lastY = -1;
    let attached = false;
    let idleId = 0;
    let timeoutId = 0;

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const y = window.scrollY || document.documentElement.scrollTop;
        if (y === lastY) return;
        lastY = y;
        node.style.transform = `translate3d(0, ${Math.min(y * 0.12, 90)}px, 0)`;
      });
    };

    const attach = () => {
      if (attached) return;
      attached = true;
      // Promote to its own layer once so scroll writes stay compositor-only.
      node.style.willChange = "transform";
      window.addEventListener("scroll", onScroll, { passive: true });
    };

    // Defer parallax wiring until idle so it does not extend index.js eval.
    if ("requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(attach, { timeout: 1_500 });
    } else {
      timeoutId = window.setTimeout(attach, 1);
    }

    return () => {
      if (idleId && "cancelIdleCallback" in window) window.cancelIdleCallback(idleId);
      if (timeoutId) window.clearTimeout(timeoutId);
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
      node.style.willChange = "";
    };
  }, []);

  return (
    <section
      id="top"
      className="relative overflow-hidden surface-dark pt-32 pb-20 lg:pt-44 lg:pb-28"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.55]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(to bottom, oklch(1 0 0 / 4%) 0 1px, transparent 1px 44px)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 right-[-10%] h-[38rem] w-[38rem] rounded-full opacity-25 blur-3xl"
        style={{ background: "var(--gradient-amber)" }}
      />
      <div className="relative mx-auto grid max-w-7xl items-start gap-14 px-6 lg:grid-cols-[1.05fr_0.95fr]">
        <div className="hero-copy">
          <div
            className="word-rise inline-flex min-h-8 max-w-full items-center gap-2 overflow-hidden whitespace-nowrap rounded-full border border-bone/20 bg-bone/5 px-4 py-2 label-eyebrow text-[0.6rem] text-bone/80"
            style={{ animationDelay: "60ms" }}
          >
            Family-Owned · Frankfort, Indiana · Since 1984
          </div>

          <HeroHeadline />

          <p
            className="word-rise mt-7 max-w-xl text-lg leading-relaxed text-bone/80"
            style={{ animationDelay: "780ms" }}
          >
            Used, new, and combo pallets for Central Indiana businesses, plus pallet removal and
            recycling, from a family operation with 40+ years in the trade.
          </p>

          <div
            className="word-rise mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
            style={{ animationDelay: "900ms" }}
          >
            <CallButton className="px-7 py-4 text-base" />
            <a
              href="#contact"
              className="group inline-flex items-center justify-center gap-2 rounded-sm border border-bone/30 px-7 py-4 font-display text-sm font-semibold tracking-[0.12em] text-bone uppercase transition-all duration-300 hover:border-amber-brand hover:text-amber-brand"
            >
              Request Pricing
              <ArrowRight
                className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                aria-hidden="true"
              />
            </a>
          </div>

          <ul
            className="word-rise mt-10 flex flex-wrap gap-x-6 gap-y-3 border-t border-bone/10 pt-6 text-sm text-bone/80"
            style={{ animationDelay: "1020ms" }}
          >
            {["Large Inventory In Stock", "Fast Turnaround", "Family-Owned Since 1984"].map(
              (chip) => (
                <li key={chip} className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 bg-amber-brand" aria-hidden="true" />
                  {chip}
                </li>
              ),
            )}
          </ul>
        </div>

        <div ref={visual} className="relative mx-auto w-full max-w-[34rem] lg:mt-4 lg:ml-auto">
          <div className="relative aspect-[460/400] w-full">
            <PalletStack className="absolute inset-0 h-full w-full" />

            <div
              className="chip-float absolute top-6 left-0 min-h-8 whitespace-nowrap rounded-sm border border-bone/20 bg-charcoal-soft/90 px-3.5 py-2 text-xs tracking-wide text-bone/85 backdrop-blur-sm"
              style={{ animationDelay: "300ms" }}
            >
              In stock <span className="text-amber-brand">✓</span>
            </div>
            <div
              className="chip-float absolute top-1/3 right-0 min-h-8 whitespace-nowrap rounded-sm border border-steel/40 bg-charcoal-soft/90 px-3.5 py-2 text-xs tracking-wide text-bone/85 backdrop-blur-sm"
              style={{ animationDelay: "1200ms" }}
            >
              Same-week turnaround
            </div>
          </div>
          <div className="mt-4 flex min-h-11 justify-center lg:justify-end">
            <span className="inline-flex min-h-11 items-center gap-2 whitespace-nowrap rounded-sm border border-amber-brand/40 bg-amber-brand/10 px-4 py-2.5 text-xs tracking-[0.1em] text-bone/85 uppercase">
              <Star className="h-3.5 w-3.5 fill-amber-brand text-amber-brand" aria-hidden="true" />
              As featured in Pallet Enterprise magazine
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function StatBand() {
  return (
    <section className="border-y border-charcoal/10 bg-bone-deep">
      <div className="mx-auto grid max-w-7xl gap-10 px-6 py-14 sm:grid-cols-2 lg:grid-cols-4">
        {[
          {
            value: <Counter to={40} suffix="+" label="40 plus years" />,
            label: "Years in Business",
          },
          { value: "Thousands", label: "Of Pallets In Stock" },
          { value: "Family", label: "Owned & Operated" },
          { value: "Featured", label: "In Pallet Enterprise" },
        ].map((stat, i) => (
          <Reveal key={i} delay={i * 110} className="border-l-2 border-amber-brand/70 pl-5">
            <div className="font-display text-4xl font-bold text-charcoal lg:text-5xl">
              {stat.value}
            </div>
            <div className="mt-2 label-eyebrow text-charcoal/70">{stat.label}</div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

const PRODUCTS = [
  {
    name: "Used Pallets",
    img: usedPallets640,
    srcSet: `${usedPallets640} 640w, ${usedPallets960} 960w, ${usedPallets1280} 1280w`,
    width: 640,
    height: 853,
    copy: "Inspected, sturdy, and budget-friendly. The workhorse choice for most operations.",
  },
  {
    name: "New Pallets",
    img: newPallets640,
    srcSet: `${newPallets640} 640w, ${newPallets800} 800w`,
    width: 640,
    height: 486,
    copy: "Built to spec for when only new will do.",
  },
  {
    name: "Combo Pallets",
    img: comboPallets640,
    srcSet: `${comboPallets640} 640w, ${comboPallets960} 960w, ${comboPallets1280} 1280w`,
    width: 640,
    height: 850,
    tag: "Best Value",
    copy: "Recycled boards on solid stringers: near-new performance at a used-pallet price.",
  },
];

function PalletSales() {
  return (
    <section id="pallets" className="relative plank-bg py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6">
        <Reveal>
          <SectionLabel>Pallet Sales</SectionLabel>
          <h2 className="mt-5 max-w-3xl text-4xl sm:text-5xl lg:text-6xl">
            The pallets you need, <span className="text-amber-brand">when</span> you need them.
          </h2>
        </Reveal>

        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {PRODUCTS.map((p, i) => (
            <Reveal key={p.name} delay={i * 130}>
              <article className="group relative flex h-full flex-col overflow-hidden rounded-sm border border-charcoal/10 bg-card shadow-[var(--shadow-plank)] transition-all duration-500 hover:-translate-y-2 hover:border-amber-brand hover:shadow-[var(--shadow-lift)]">
                {p.tag && (
                  <span className="absolute top-4 right-4 z-10 rounded-sm bg-steel px-3 py-1.5 text-[0.65rem] font-semibold tracking-[0.14em] text-bone uppercase">
                    {p.tag}
                  </span>
                )}
                <MaskImage
                  src={p.img}
                  srcSet={p.srcSet}
                  alt={`${p.name} at Livengood Pallet Service in Frankfort, Indiana`}
                  delay={i * 130}
                  width={p.width}
                  height={p.height}
                  sizes="(min-width: 1024px) 30vw, 100vw"
                  className="aspect-[4/3] w-full"
                />
                <div className="flex flex-1 flex-col p-7">
                  <h3 className="text-2xl text-charcoal">{p.name}</h3>
                  <p className="mt-3 flex-1 text-[0.975rem] leading-relaxed text-muted-foreground">
                    {p.copy}
                  </p>
                  <a
                    href={PHONE_HREF}
                    className="mt-6 inline-flex items-center gap-2 font-display text-sm font-semibold tracking-[0.12em] text-charcoal uppercase transition-colors group-hover:text-amber-brand"
                  >
                    Call for Pricing
                    <ArrowRight
                      className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5"
                      aria-hidden="true"
                    />
                  </a>
                  <span className="mt-4 block h-[2px] w-0 bg-amber-brand transition-all duration-500 group-hover:w-full" />
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={120}>
          <p className="mt-12 max-w-2xl text-lg text-charcoal/70">
            Tell us your size, grade, and quantity — we'll give you a straight price and a fast
            answer.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function MobileCallBar() {
  return (
    <nav
      aria-label="Call or request pricing"
      className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-2 gap-2 border-t border-bone/10 bg-charcoal/95 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur-lg lg:hidden"
    >
      <a
        href={PHONE_HREF}
        className="call-pulse inline-flex min-h-11 items-center justify-center gap-2 rounded-sm bg-amber-brand py-3.5 font-display text-sm font-semibold tracking-[0.1em] text-primary-foreground uppercase"
      >
        <Phone className="h-4 w-4 handset-ring" strokeWidth={2.5} aria-hidden="true" />
        Call Now
      </a>
      <a
        href="#contact"
        className="inline-flex min-h-11 items-center justify-center rounded-sm border border-bone/40 py-3.5 font-display text-sm font-semibold tracking-[0.1em] text-bone uppercase"
      >
        Request Pricing
      </a>
    </nav>
  );
}

function Index() {
  return (
    <div className="min-h-screen bg-bone">
      <Nav />
      <main id="main-content" tabIndex={-1}>
        <Hero />
        <StatBand />
        <PalletSales />
        <Suspense fallback={null}>
          <HomeBelow />
        </Suspense>
      </main>
      <MobileCallBar />
    </div>
  );
}
